// Chalk Explainer Ad's form (live's `chalk-explainer-ad-form/library-view`, spec
// G14-chalk-explainer-ad PR d2; not captured: no agent has browsed live since 2026-10-22, so built
// from live's code): live's staged form in two approvals. The script stage picks the product,
// writes the script and the board (the server's pack reader and director) and approves them; the
// storyboard shows the five images (`ChalkStoryboard.tsx`). The draft is the server's
// (`chalk-draft.ts`). "Generate final video" starts the run (`templates/chalk_explainer_ad.py`).
import { Group, Loader, Stack, Text, Tooltip } from "@mantine/core";
import { Layers, Monitor, Smartphone } from "lucide-react";
import { useState, type ReactNode } from "react";

import { useListTemplateCaptionStyles } from "../../../api/generated/endpoints/generation/generation";
import type { Job } from "../../../api/generated/model";
import {
  VideoTemplateQuote,
  VideoTemplateRequest,
  chalkExplainerAdInputsCtaMax,
  chalkExplainerAdInputsProblemMax,
  chalkExplainerAdInputsPronunciationMax,
  type ChalkExplainerAdInputsOutput,
} from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ChalkScriptSection } from "./ChalkScript";
import { ChalkStoryboard } from "./ChalkStoryboard";
import { ReuseSwitch, type TemplateFormProps } from "./kit";
import { InputChip } from "./PodcastScript";
import { Submit } from "./staged";
import { Subtitles } from "./Subtitles";
import { StreetProductTile, type ProductWords } from "./StreetTiles";
import { useChalkDraft, videoBody, type DraftWork, type VideoSettings } from "./chalk-draft";
import { PICK_PRODUCT, UNREADABLE_PACK, scriptIssue, scriptWarnings } from "./chalk-script";
import classes from "./podcast.module.css";

/** Live's words (`library-view/constants.ts`). */
const PRODUCT_WORDS: ProductWords = {
  tile:
    "The product and the exact photo of it the ad is built on. Pick one of your products and its " +
    "packshot, or upload a packshot — the product the hand raises at the end and the end card shows.",
  name: "The product name the script and the end card use. Leave empty and it is read off the packshot.",
  text:
    "A description or the product page. Every product fact in the script is quoted from it — leave " +
    "empty and the words printed on the pack are used.",
};
const SUBTITLES_TOOLTIP =
  "Burned-in captions of the narrator in short lines, over the board clips only, not the end card.";
const TAKES_TOOLTIP =
  "Takes of each board clip. Every take is paid for; the one whose narration matches the script " +
  "best is used.";
const RESOLUTION_TOOLTIP = "Resolution of every clip. 720p costs about 45% of 1080p.";
const FORMAT_TOOLTIP = "Every frame and clip is vertical 9:16.";
const APPROVE_SCRIPT = "Approve script";
const GENERATE_VIDEO = "Generate final video";
const GENERATE_SCRIPT_REASON = "Generate the script";
const STILLS_PENDING = "Wait for every image";
const HANDOFF_NOTICE = "Your chalk explainer ad is finishing in My Generations — you can close this panel now.";
const RESOLUTIONS: readonly ChalkExplainerAdInputsOutput["resolution"][] = ["1080p", "720p"];
const TAKES = [
  { value: "1", label: "1 take" },
  { value: "2", label: "2 takes" },
  { value: "3", label: "3 takes" },
];
const CHIPS = [
  {
    field: "problem",
    label: "Problem",
    placeholder: "You wake up tired (optional)",
    tooltip:
      'The buyer\'s everyday situation the ad opens on, e.g. "you wake up tired". Leave empty and ' +
      "the director picks the one the product page is most clearly about.",
    maxLength: chalkExplainerAdInputsProblemMax,
  },
  {
    field: "cta",
    label: "CTA",
    placeholder: "Shop now (optional)",
    tooltip: "The button on the end card, word for word. Leave empty and the director writes a short one.",
    maxLength: chalkExplainerAdInputsCtaMax,
  },
  {
    field: "pronunciation",
    label: "Pronunciation",
    placeholder: "YOO-zoo (optional)",
    tooltip:
      "How your brand name is said, e.g. YOO-zoo. Leave empty and the director decides what needs a " +
      "respelling.",
    maxLength: chalkExplainerAdInputsPronunciationMax,
  },
] as const;

/** Live's progress line under the form (`run-progress.ts`). */
export function progressOf(work: DraftWork, drawn: number, total: number): string[] {
  if (work.reading) return ["Reading the pack"];
  if (work.writing) return ["Writing the script"];
  return [
    ...(work.boarding ? ["Writing the board"] : []),
    ...(work.drawing ? [`Generating images · ${drawn}/${total}`] : []),
  ];
}

/**
 * Live's form: "Reuse example" (off and disabled: the card has no example yet), the stage, and in
 * the footer the subtitles, the takes, the resolution, the 9:16 format and the round start,
 * "Approve script" then "Generate final video", whose tooltip says what it waits for. The
 * subtitles and the resolution stay editable on the storyboard, as live's; the takes do not.
 */
export function ChalkExplainerAdForm(props: TemplateFormProps) {
  const { teamId, brandId, above, onStarted, example } = props;
  const [collapsed, setCollapsed] = useState(false);
  const flow = useChalkDraft(teamId, brandId);
  const styles = useListTemplateCaptionStyles(teamId).data;
  const [productAsked, setProductAsked] = useState(false);
  const [settings, setSettings] = useState<VideoSettings | null>(null);
  // The run started here (live's hand-off notice); a change to the draft ends it.
  const [started, setStarted] = useState<string | null>(null);
  const { form, draft } = flow;
  const writing = flow.work.reading || flow.work.writing;
  const approved = draft !== undefined && draft.stage !== "script";
  const shownSettings: VideoSettings | null =
    form === null
      ? null
      : ((approved ? settings : null) ?? {
          subtitles: form.inputs.subtitles,
          caption_position: form.inputs.caption_position,
          caption_style_id: form.inputs.caption_style_id,
          resolution: form.inputs.resolution,
        });
  const video = draft === undefined || shownSettings === null ? null : videoBody(draft, shownSettings);
  const videoRequest = VideoTemplateRequest.safeParse(video);
  const videoQuote = VideoTemplateQuote.safeParse(video);
  const videoStart = usePricedStart({
    teamId,
    brandId,
    quote: videoQuote.success ? videoQuote.data : undefined,
    request: videoRequest.success ? videoRequest.data : undefined,
    onStarted: (job: Job) => {
      setStarted(job.id);
      onStarted(job);
    },
  });
  if (!approved && settings !== null) setSettings(null);
  if (!approved && started !== null) setStarted(null);

  const panel = (body: ReactNode, footer?: ReactNode) => (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={null} checked={false} onChange={() => undefined} />}
      summary={<Text size="sm">Chalk Explainer Ad</Text>}
      footer={footer}
    >
      {body}
    </GlassPanel>
  );
  if (flow.read.isError) return panel(<ErrorAlert error={flow.read.error} />);
  if (draft === undefined || form === null || shownSettings === null) {
    return panel(<Loader size="sm" aria-label="Loading the draft" />);
  }

  const { inputs, script } = form;
  const setInputs = (change: Partial<ChalkExplainerAdInputsOutput>) => flow.editInputs(change);
  const changeSettings = (change: Partial<VideoSettings>) => {
    if (approved) setSettings({ ...shownSettings, ...change });
    else setInputs(change);
  };
  const locked = !flow.editable;
  const drawn = draft.stills.filter((each) => each.status === "done").length;
  const progress = progressOf(flow.work, drawn, draft.stills.length);
  const issue = scriptIssue(script, inputs.cta);

  let submit;
  if (approved) {
    const ready = draft.stills.length > 0 && drawn === draft.stills.length && !flow.work.boarding;
    submit = (
      <Submit
        label={GENERATE_VIDEO}
        reasons={ready ? [] : [STILLS_PENDING]}
        disabled={!ready || flow.busy || !videoStart.canStart || videoStart.start.isPending || started !== null}
        loading={videoStart.start.isPending}
        onClick={videoStart.startJob}
      />
    );
  } else {
    const reasons = [...(inputs.product ? [] : [PICK_PRODUCT]), ...(script === null ? [GENERATE_SCRIPT_REASON] : [])];
    submit = (
      <Submit
        label={APPROVE_SCRIPT}
        reasons={reasons}
        disabled={reasons.length > 0 || issue !== null || flow.busy}
        loading={flow.approving}
        onClick={flow.approve}
      />
    );
  }

  const footer = (
    <Group justify="space-between" wrap="nowrap" gap={12}>
      <div className={classes.settings}>
        <Subtitles
          value={{
            on: shownSettings.subtitles,
            position: shownSettings.caption_position,
            styleId: shownSettings.caption_style_id || (styles?.default ?? ""),
          }}
          styles={styles?.styles}
          tip={SUBTITLES_TOOLTIP}
          disabled={flow.busy}
          onChange={(value) =>
            changeSettings({
              subtitles: value.on,
              caption_position: value.position,
              caption_style_id: value.styleId,
            })
          }
        />
        <Choice
          setting="Takes"
          icon={<Layers size={16} strokeWidth={1.5} />}
          tip={TAKES_TOOLTIP}
          width={96}
          variant="footer"
          value={String(inputs.takes)}
          options={TAKES}
          disabled={locked}
          onChange={(next) => setInputs({ takes: Number(next) })}
        />
        <Choice
          setting="Resolution"
          icon={<Monitor size={16} strokeWidth={1.5} />}
          tip={RESOLUTION_TOOLTIP}
          width={80}
          variant="footer"
          value={shownSettings.resolution}
          options={RESOLUTIONS.map((value) => ({ value, label: value }))}
          disabled={flow.busy}
          onChange={(next) => {
            const resolution = RESOLUTIONS.find((each) => each === next);
            if (resolution !== undefined) changeSettings({ resolution });
          }}
        />
        <Tooltip label={FORMAT_TOOLTIP} multiline maw={280} withArrow>
          <span className={classes.setting}>
            <Smartphone size={16} strokeWidth={1.5} aria-hidden />
            9:16
          </span>
        </Tooltip>
      </div>
      {submit}
    </Group>
  );

  const refused = videoStart.start.error ?? videoStart.quoteError ?? flow.failure;
  const status = (
    <>
      {draft.problem === null ? null : (
        <Text className={classes.problem} role="alert">
          {draft.problem}
        </Text>
      )}
      {refused === null ? null : <ErrorAlert error={refused} />}
      {progress.map((line) => (
        <output key={line} className={classes.progress}>
          <Loader size={14} aria-hidden />
          {line}
        </output>
      ))}
      {started === null ? null : (
        <Text component="output" size="sm">
          Generation started. {HANDOFF_NOTICE}
        </Text>
      )}
    </>
  );
  if (approved) {
    return panel(
      <div className={classes.content}>
        <ChalkStoryboard
          stills={draft.stills}
          board={draft.board}
          cues={draft.cues}
          cueWarnings={draft.cue_warnings}
          boarding={flow.work.boarding}
          busy={flow.busy}
          resetting={flow.resetting}
          onRegenerate={(name) => {
            setStarted(null);
            flow.regenerate(name);
          }}
          onBackToScript={flow.backToScript}
          onStartOver={flow.startOver}
        />
        {status}
      </div>,
      footer,
    );
  }

  const warnings = [...(draft.pack_warning ? [UNREADABLE_PACK] : []), ...scriptWarnings(draft)];
  const body = (
    <div className={classes.content}>
      <Stack gap={12}>
        <div className={classes.tiles}>
          <StreetProductTile
            teamId={teamId}
            brandId={brandId}
            pick={inputs.product ?? null}
            typed={{ product_name: inputs.product_name, product_text: inputs.product_text }}
            disabled={locked}
            missing={productAsked}
            exampleUrls={example?.urls ?? {}}
            words={PRODUCT_WORDS}
            onChange={(product, typed) => setInputs({ product, ...typed, product_category: "" })}
          />
        </div>
        <div className={classes.chips}>
          {CHIPS.map((chip) => (
            <InputChip
              key={chip.field}
              label={chip.label}
              value={inputs[chip.field]}
              placeholder={chip.placeholder}
              tooltip={chip.tooltip}
              maxLength={chip.maxLength}
              disabled={locked}
              onChange={(value) => {
                const change: Partial<ChalkExplainerAdInputsOutput> = {};
                change[chip.field] = value;
                setInputs(change);
              }}
            />
          ))}
        </div>
        <ChalkScriptSection
          script={script}
          timing={draft.timing}
          writing={writing}
          locked={locked}
          issue={script !== null && !writing ? issue : null}
          warnings={warnings}
          onGenerate={() => {
            if (!inputs.product) {
              setProductAsked(true);
              return;
            }
            flow.generateScript();
          }}
          onChange={(next) => flow.edit({ script: next })}
        />
      </Stack>
      {status}
    </div>
  );
  return panel(body, footer);
}
